import React, { useCallback, useMemo, useState } from "react";
import { cloneDeep, get } from "lodash";
import { useFieldArray } from "react-hook-form";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import EmptyState from "../illustrations/EmptyState";
import { Button, H3, Input } from "../wombat-ui";
import Select from "../wombat-ui/components/Form/Select";
import { TemplateFormElementModal } from "./TemplateFormElementModal";
import {
  TemplateSectionForm,
  TemplateSectionFormInput,
} from "./TemplateSectionForm";
import { PlusIcon } from "../icons";

const TemplateForm = ({
  register,
  unregister,
  setValue,
  getValues,
  defaultValue,
  shouldSelectReferent = false,
  errors,
  control,
  formState,
  reset,
  name,
}) => {
  const { company } = useViewer()
  const [focusedIdx, setFocusedIdx] = useState<string>(null);
  const { fields, append, insert } = useFieldArray<
    TemplateSectionFormInput,
    "key"
  >({
    control,
    name: `${name}.templateSectionsAttributes`,
    keyName: "key",
  });

  const {
    company: { contractTemplates },
  } = useViewer();
  let sectionIndexDampener = 0;

  const t = useTranslations();
  const [
    templateFormElementModalOpen,
    setTemplateFormElementModalOpen,
  ] = useState({ open: false, insertIndex: -1 });

  const addElementButton = useMemo(
    () => (
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center p-6 pb-6 space-y-3 rounded cursor-pointer">
          {fields.length === 0 && <EmptyState />}
          <Button
            id={`${name}.addElementButton`}
            onClick={() =>
              setTemplateFormElementModalOpen({ open: true, insertIndex: -1 })
            }
          >
            {t("templates.createElement")}
          </Button>
        </div>
      </div>
    ),
    [fields, name]
  );

  const handleFocus = useCallback((sectionIdx: number, questionIdx: number) => {
    setFocusedIdx(`${sectionIdx}-${questionIdx}`);
  }, []);

  const handleRemoveSection = useCallback(
    (index) => {
      // clone it to make mutating the value easier
      const values = cloneDeep(getValues());
      get(values, name).templateSectionsAttributes.splice(index, 1);
      reset(values);
    },
    [name]
  );

  return (
    <>
      <div className="flex flex-col">
        <H3>{t("mads.title")}</H3>
        <div className="pt-3">
          <Input
            srOnlyLabel
            id="title"
            ref={register({ required: t("templates.titleRequired") as string })}
            name={`${name}.title`}
            label={t("mads.title") as string}
            error={errors?.title}
          />
        </div>
        {company.contractsEnabled && (
          <>
            <H3 description={t("mads.chooseContract")}>
              {t("mads.contractsTitle")}
            </H3>
            <div className="pt-3">
              <Select
                label={t("mads.chooseContract") as string}
                srOnlyLabel
                id="select-templates-contracts"
                searchable
                placeholder={t("mads.searchContract") as string}
                multiple
                defaultValue={defaultValue?.contractTemplates || []}
                searchOption={{
                  keys: ["title"],
                  threshold: 0.2,
                  useExtendedSearch: true,
                }}
                items={contractTemplates}
                RHF={{
                  register,
                  unregister,
                  setValue,
                  name: `${name}.contractTemplateIds`,
                }}
                renderItem={(item) => item.title}
                getValue={(item) => item.id}
              />
            </div>
          </>
        )}
      </div>
      <H3>{t("mads.surveyContent")}</H3>
      {fields.length === 0 ? (
        addElementButton
      ) : (
        <>
          {fields.map((field, index) => {
            if (field.type === "TemplateSectionBlank") sectionIndexDampener++;
            return (
              <div key={field.key}>
                <div className="relative flex justify-center sm:mx-8 group">
                  <div className="absolute w-full h-px inset-y-1/2 bg-light-100 group-hover:bg-light-500" />
                  <div className="">
                    <Button
                      id={`insert_section_${index}`}
                      icon={PlusIcon}
                      variant="subtle"
                      scale="xs"
                      onClick={() =>
                        setTemplateFormElementModalOpen({
                          open: true,
                          insertIndex: index,
                        })
                      }
                    />
                  </div>
                </div>
                <TemplateSectionForm
                  sectionDisplayIndex={index - sectionIndexDampener}
                  index={index}
                  control={control}
                  register={register}
                  unregister={unregister}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors?.templateSectionsAttributes?.[index]}
                  defaultValue={field}
                  formState={formState}
                  focusedIdx={focusedIdx}
                  handleFocus={handleFocus}
                  shouldSelectReferent={shouldSelectReferent}
                  onRemove={handleRemoveSection}
                  reset={reset}
                  name={`${name}.templateSectionsAttributes[${index}]`}
                />
              </div>
            );
          })}
          <div className="flex justify-center">{addElementButton}</div>
        </>
      )}
      <TemplateFormElementModal
        insideSection={false}
        open={templateFormElementModalOpen.open}
        onClose={() =>
          setTemplateFormElementModalOpen({ open: false, insertIndex: -1 })
        }
        handleFocus={handleFocus}
        setValue={setValue}
        getValues={getValues}
        fields={fields}
        append={append}
        insert={insert}
        reset={reset}
        name={name}
        insertIndex={templateFormElementModalOpen.insertIndex}
      />
    </>
  );
};

export default React.memo(TemplateForm);
